import React, { useState, useEffect,useRef } from 'react';
import { Menu, X, Phone, Mail, Clock, Wrench,Scissors, Home, AlertCircle, Zap, Star, Award, Shield, Calculator, Search,Truck, ArrowLeft, Package, CloudRain, ChevronRight, ArrowRight, Users, Building, CheckCircle, TrendingUp, ChevronLeft, Grid, List, ShoppingCart, HardHat, PaintBucket, Droplets, Thermometer, Layers, Send, Calendar, DollarSign, Bath, Target, Hammer, Handshake, Cpu, Microscope, Quote, MapPin} from 'lucide-react';


import { Trabajo, Producto, Servicio, PhoneNumber } from './types';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import CookieConsent from './components/CookieConsent';
import CookieSettingsModal from './components/CookieSettingsModal';
import AccessibilityModal from './components/AccessibilityModal';
import HomePage from './pages/HomePage';
import ServiciosPage from './pages/ServiciosPage';
import TrabajosPage from './pages/TrabajosPage';
import ProductosPage from './pages/ProductosPage';
import ContactoPage from './pages/ContactoPage';
import PhoneSelector from './components/PhoneSelector';
import PoliticaPrivacidad from './pages/PoliticaPrivacidad';
import PoliticaCookies from './pages/PoliticaCookies';
import AvisoLegal from './pages/AvisoLegal';
import WhatsAppButton from './components/WhatsAppButton';

// ==================== SEO POR PÁGINA ====================
const PAGE_META: Record<string, { title: string; description: string }> = {
  'home': {
    title: 'José Luis Construcciones | Reformas y Construcción en Martos, Jaén',
    description: 'Empresa de reformas integrales, construcción y rehabilitación en Martos, Jaén. Desde 1998. Presupuestos sin compromiso.'
  },
  'servicios': {
    title: 'Servicios de Reformas y Construcción en Martos, Jaén | José Luis Construcciones',
    description: 'Reformas integrales, baños, cocinas, fontanería, electricidad, aerotermia, piscinas e impermeabilización en Martos y toda la provincia de Jaén.'
  },
  'proyectos': {
    title: 'Proyectos y Trabajos Realizados | José Luis Construcciones',
    description: 'Descubre nuestros trabajos de reformas y construcción realizados en Martos y Jaén. Fotos de antes y después de proyectos reales.'
  },
  'productos': {
    title: 'Productos de Baño y Piscina | José Luis Construcciones',
    description: 'Venta de accesorios de baño, espejos, mamparas y productos para piscinas en Martos, Jaén.'
  },
  'contacto': {
    title: 'Contacto y Presupuesto sin Compromiso | José Luis Construcciones',
    description: 'Pide tu presupuesto sin compromiso. Teléfono 953 03 95 67. Calle Ingeniero García Pimentel 37, Martos (Jaén).'
  },
  'privacidad': {
    title: 'Política de Privacidad | José Luis Construcciones',
    description: 'Política de privacidad y protección de datos de José Luis Construcciones.'
  },
  'cookies': {
    title: 'Política de Cookies | José Luis Construcciones',
    description: 'Información sobre el uso de cookies en la web de José Luis Construcciones.'
  },
  'aviso-legal': {
    title: 'Aviso Legal | José Luis Construcciones',
    description: 'Aviso legal y condiciones de uso de la web de José Luis Construcciones.'
  }
};

// Actualiza el consentimiento de Google Analytics (Consent Mode v2)
const applyAnalyticsConsent = (granted: boolean) => {
  const w = window as any;
  if (typeof w.gtag === 'function') {
    w.gtag('consent', 'update', {
      analytics_storage: granted ? 'granted' : 'denied',
      ad_storage: 'denied',
      ad_user_data: 'denied',
      ad_personalization: 'denied'
    });
  }
};

// ==================== COMPONENTE PRINCIPAL ====================
export default function App() {
  const getInitialPage = () => {
    let path = window.location.pathname.replace(/^\/|\/$/g, '').toLowerCase();
    
    // Redirigir el enlace antiguo de "/trabajos" a la nueva ruta "/proyectos"
    if (path === 'trabajos') {
      path = 'proyectos';
      window.history.replaceState(null, '', '/proyectos');
    }
    
    const validPages = ['home', 'servicios', 'proyectos', 'productos', 'contacto', 'privacidad', 'cookies', 'aviso-legal'];
    return validPages.includes(path) ? path : 'home';
  };

  const [currentPage, setCurrentPage] = useState(getInitialPage());
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [showCookieConsent, setShowCookieConsent] = useState(false);
  const [showCookieSettings, setShowCookieSettings] = useState(false);
  const [showAccessibilityModal, setShowAccessibilityModal] = useState(false);
  const [cookiePreferences, setCookiePreferences] = useState({
    necessary: true,
    analytics: false,
    marketing: false
  });

  // Control del scroll y del botón atrás del navegador
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    
    const handlePopState = () => {
      setCurrentPage(getInitialPage());
    };

    window.addEventListener('scroll', handleScroll);
    window.addEventListener('popstate', handlePopState);
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('popstate', handlePopState);
    };
  }, []);

  // Actualizar canonical, título y meta descripción de la página para SEO
  const updateCanonical = (page: string) => {
    const url = page === 'home'
      ? 'https://joseluisconstrucciones.com/'
      : `https://joseluisconstrucciones.com/${page}`;

    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.rel = 'canonical';
      document.head.appendChild(canonical);
    }
    canonical.href = url;

    const meta = PAGE_META[page] || PAGE_META['home'];
    document.title = meta.title;
    const metaDesc = document.querySelector('meta[name="description"]') as HTMLMetaElement;
    if (metaDesc) metaDesc.content = meta.description;
    const ogTitle = document.querySelector('meta[property="og:title"]') as HTMLMetaElement;
    if (ogTitle) ogTitle.content = meta.title;
    const ogDesc = document.querySelector('meta[property="og:description"]') as HTMLMetaElement;
    if (ogDesc) ogDesc.content = meta.description;
    const ogUrl = document.querySelector('meta[property="og:url"]') as HTMLMetaElement;
    if (ogUrl) ogUrl.content = url;
  };

  // Actualizar canonical en la carga inicial
  useEffect(() => {
    updateCanonical(currentPage);
  }, [currentPage]);

  // Función para navegar
  const navigateToPage = (page: string) => {
    setCurrentPage(page);
    setIsMenuOpen(false);
    window.scrollTo(0, 0);
    window.history.pushState(null, '', `/${page === 'home' ? '' : page}`);
    updateCanonical(page);
  };

  // Aceptar cookies
  const acceptCookies = () => {
    setCookiePreferences({
      necessary: true,
      analytics: true,
      marketing: true
    });
    setShowCookieConsent(false);
    localStorage.setItem('cookieConsent', 'accepted');
    localStorage.setItem('cookiePreferences', JSON.stringify({
      necessary: true,
      analytics: true,
      marketing: true
    }));
    applyAnalyticsConsent(true);
  };

  // Rechazar cookies no necesarias
  const rejectCookies = () => {
    setCookiePreferences({
      necessary: true,
      analytics: false,
      marketing: false
    });
    setShowCookieConsent(false);
    localStorage.setItem('cookieConsent', 'rejected');
    localStorage.setItem('cookiePreferences', JSON.stringify({
      necessary: true,
      analytics: false,
      marketing: false
    }));
    applyAnalyticsConsent(false);
  };

  // Guardar preferencias personalizadas
  const saveCookiePreferences = () => {
    setShowCookieConsent(false);
    setShowCookieSettings(false);
    localStorage.setItem('cookieConsent', 'custom');
    localStorage.setItem('cookiePreferences', JSON.stringify(cookiePreferences));
    applyAnalyticsConsent(cookiePreferences.analytics);
  };

  // Abrir configuración de cookies
  const openCookieSettings = () => {
    setShowCookieSettings(true);
  };

  // Cerrar configuración de cookies
  const closeCookieSettings = () => {
    setShowCookieSettings(false);
  };

  // Cambiar preferencias de cookies
  const handlePreferenceChange = (key: string, value: boolean) => {
    setCookiePreferences(prev => ({
      ...prev,
      [key]: value
    }));
  };

  // Abrir modal de accesibilidad
  const openAccessibilityModal = () => {
    setShowAccessibilityModal(true);
  };

  // Cerrar modal de accesibilidad
  const closeAccessibilityModal = () => {
    setShowAccessibilityModal(false);
  };

  // Verificar consentimiento de cookies
  useEffect(() => {
    const consent = localStorage.getItem('cookieConsent');
    const preferences = localStorage.getItem('cookiePreferences');

    // Solo mostrar el modal si NO hay un consentimiento válido guardado
    if (consent !== 'accepted' && consent !== 'rejected' && consent !== 'custom') {
      setShowCookieConsent(true);
    } else {
      // Si hay consentimiento válido, cargar preferencias guardadas
      if (preferences) {
        const parsed = JSON.parse(preferences);
        setCookiePreferences(parsed);
        applyAnalyticsConsent(!!parsed.analytics);
      } else {
        applyAnalyticsConsent(consent === 'accepted');
      }
    }
  }, []);

  // Renderizar página actual
  const renderPage = () => {
    switch (currentPage) {
      case 'home': return <HomePage navigateToPage={navigateToPage} />;
      case 'servicios': return <ServiciosPage navigateToPage={navigateToPage} PhoneSelector={PhoneSelector} />;
      case 'proyectos': return <TrabajosPage navigateToPage={navigateToPage} />;
      case 'productos': return <ProductosPage navigateToPage={navigateToPage} />;
      case 'contacto': return <ContactoPage />;
      case 'privacidad': return <PoliticaPrivacidad navigateToPage={navigateToPage} />;
      case 'cookies': return <PoliticaCookies navigateToPage={navigateToPage} />;
      case 'aviso-legal': return <AvisoLegal navigateToPage={navigateToPage} />;
      default: return <HomePage navigateToPage={navigateToPage} />;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <style>{`
        @keyframes fade-in {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        
        .animate-fade-in {
          animation: fade-in 0.6s ease-out forwards;
        }
        
        .scroll-animate {
          opacity: 0;
        }
        
        /* Custom scrollbar */
        ::-webkit-scrollbar {
          width: 10px;
        }
        
        ::-webkit-scrollbar-track {
          background: #f1f1f1;
        }
        
        ::-webkit-scrollbar-thumb {
          background: #3b82f6;
          border-radius: 5px;
        }
        
        ::-webkit-scrollbar-thumb:hover {
          background: #2563eb;
        }
        
        /* Smooth transitions (Tailwind uses specific classes, avoiding * for performance) */
        a, button, input, select, textarea, .card-hover, img {
          transition: background-color 0.2s ease, border-color 0.2s ease, transform 0.2s ease, box-shadow 0.2s ease, opacity 0.2s ease;
        }
        
        /* Card hover effects */
        .card-hover {
          transition: all 0.3s ease;
        }
        
        .card-hover:hover {
          transform: translateY(-5px);
          box-shadow: 0 20px 40px rgba(0,0,0,0.1);
        }
        
        /* Image overlay */
        .image-overlay {
          background: linear-gradient(to bottom, transparent 0%, rgba(0,0,0,0.7) 100%);
        }
        
        /* Gradient text */
        .gradient-text {
          background: linear-gradient(135deg, #2563eb 0%, #06b6d4 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }
        
        /* Glass effect */
        .glass {
          background: rgba(255, 255, 255, 0.85);
          backdrop-filter: blur(10px);
          border: 1px solid rgba(255, 255, 255, 0.3);
        }
      `}</style>

      <style>{`
        /* ... estilos existentes ... */
        
        /* Animación de highlight */
        @keyframes highlight {
          0% { 
            box-shadow: 0 0 0 0 rgba(59, 130, 246, 0.5);
            transform: scale(1);
          }
          50% { 
            box-shadow: 0 0 20px 10px rgba(59, 130, 246, 0.3);
            transform: scale(1.02);
          }
          100% { 
            box-shadow: 0 0 0 0 rgba(59, 130, 246, 0);
            transform: scale(1);
          }
        }
        
        .highlight-animation {
          animation: highlight 1s ease-out 2;
        }
      `}</style>
      
      <a href="#main-content" className="sr-only focus:not-sr-only focus:fixed focus:top-4 focus:left-4 focus:z-[9999] focus:bg-blue-600 focus:text-white focus:px-4 focus:py-2 focus:rounded-lg focus:shadow-lg focus:outline-none">
        Saltar al contenido principal
      </a>

      <Navbar 
        currentPage={currentPage} 
        navigateToPage={navigateToPage}
        isMenuOpen={isMenuOpen}
        setIsMenuOpen={setIsMenuOpen}
        scrolled={scrolled}
      />
      
      <main id="main-content" className="pt-20" tabIndex={-1}>
        {renderPage()}
      </main>
      
      <Footer navigateToPage={navigateToPage} openAccessibilityModal={openAccessibilityModal} />

      {/* Botón flotante de WhatsApp */}
      <WhatsAppButton />

      {/* Consentimiento de cookies */}
      {showCookieConsent && <CookieConsent onAccept={acceptCookies} onReject={rejectCookies} onConfigure={openCookieSettings} navigateToPage={navigateToPage} />}

      {/* Modal de configuración de cookies */}
      <CookieSettingsModal 
        isOpen={showCookieSettings}
        onClose={closeCookieSettings}
        preferences={cookiePreferences}
        onPreferencesChange={handlePreferenceChange}
        onSave={saveCookiePreferences}
      />

      {/* Modal de accesibilidad */}
      <AccessibilityModal isOpen={showAccessibilityModal} onClose={closeAccessibilityModal} />
    </div>
  );
}
